import { CapturedNetworkRequest, NetworkRecordOptions, PostHogConfig } from '../../../types'
import { isFunction, isNullish, isString, isUndefined } from '@posthog/core'
import { convertToURL } from '@posthog/browser-common/utils/request-utils'
import { logger } from '@posthog/browser-common/utils/logger'
import { shouldCaptureValue } from '@posthog/browser-common/utils/autocapture-utils'
import { each } from '@posthog/browser-common/utils/general-utils'

const LOGGER_PREFIX = '[SessionRecording]'

const REDACTED = 'redacted'

// the hard ceiling for a recorded request/response body — 1MB, even if a larger limit is configured
export const MAX_PAYLOAD_SIZE_BYTES = 1000000

// the smallest of the 1MB ceiling or the configured limit if there is one
export function effectivePayloadLimitBytes(options: Pick<NetworkRecordOptions, 'payloadSizeLimitBytes'>): number {
    return Math.min(MAX_PAYLOAD_SIZE_BYTES, options.payloadSizeLimitBytes ?? MAX_PAYLOAD_SIZE_BYTES)
}

export const defaultNetworkOptions: Required<NetworkRecordOptions> = {
    initiatorTypes: [
        'audio',
        'beacon',
        'body',
        'css',
        'early-hint',
        'embed',
        'fetch',
        'frame',
        'iframe',
        'icon',
        'image',
        'img',
        'input',
        'link',
        'navigation',
        'object',
        'ping',
        'script',
        'track',
        'video',
        'xmlhttprequest',
    ],
    maskRequestFn: (data: CapturedNetworkRequest) => data,
    recordHeaders: false,
    recordBody: false,
    recordInitialRequests: false,
    recordPerformance: false,
    performanceEntryTypeToObserve: [
        // 'event', // This is too noisy as it covers all browser events
        'first-input',
        // 'mark', // Mark is used too liberally. We would need to filter for specific marks
        // 'measure', // Measure is used too liberally. We would need to filter for specific measures
        'navigation',
        'paint',
        'resource',
    ],
    payloadSizeLimitBytes: MAX_PAYLOAD_SIZE_BYTES,
    payloadHostDenyList: [
        '.lr-ingest.io',
        '.ingest.sentry.io',
        '.clarity.ms',
        // NB no leading dot here
        // GA4/gtag beacons go to *.google-analytics.com; with Google Signals on they also hit
        // analytics.google.com (region1.analytics.google.com/g/collect), so deny both
        'google-analytics.com',
        'analytics.google.com',
        // New Relic browser agent (bam + bam-cell)
        'nr-data.net',
        // Datadog browser RUM intake
        'datadoghq.com',
        'datadoghq.eu',
        'ddog-gov.com',
        // other third-party analytics / session-replay vendors whose telemetry has no replay value
        'segment.io',
        'rudderstack.com',
        'amplitude.com',
        'mixpanel.com',
        // Hotjar uses both .com and .io for data collection
        'hotjar.com',
        'hotjar.io',
        'fullstory.com',
    ],
    streamNetworkBody: false,
}

const HEADER_DENY_LIST = [
    'authorization',
    'x-forwarded-for',
    'authorization',
    'cookie',
    'set-cookie',
    'x-api-key',
    'x-real-ip',
    'remote-addr',
    'forwarded',
    'proxy-authorization',
    'x-csrf-token',
    'x-csrftoken',
    'x-xsrf-token',
]

const PAYLOAD_CONTENT_DENY_LIST = [
    'password',
    'secret',
    'passwd',
    'api_key',
    'apikey',
    'auth',
    'credentials',
    'mysql_pwd',
    'privatekey',
    'private_key',
    'token',
]

// substrings that mark a header as credential-bearing - catches custom names
// (e.g. x-gist-encoded-user-token) that aren't in the exact deny list above
const HEADER_DENY_SUBSTRINGS = [
    'auth',
    'token',
    'secret',
    'session',
    'api-key',
    'apikey',
    'api_key',
    'credential',
    'password',
    'passwd',
    'cookie',
    'csrf',
    'xsrf',
]

const isDeniedHeader = (header: string): boolean => {
    const lower = header.toLowerCase()
    return HEADER_DENY_LIST.includes(lower) || HEADER_DENY_SUBSTRINGS.some((s) => lower.includes(s))
}

const redactDeniedHeaders = (headers: CapturedNetworkRequest['requestHeaders']): void => {
    if (!isNullish(headers)) {
        each(Object.keys(headers), (header) => {
            if (isDeniedHeader(header)) {
                headers[header] = REDACTED
            }
        })
    }
}

// we always remove headers on the deny list because we never want to capture this sensitive data.
// applies to both request and response headers (e.g. set-cookie is a response header)
const removeAuthorizationHeader = (data: CapturedNetworkRequest): CapturedNetworkRequest => {
    redactDeniedHeaders(data.requestHeaders)
    redactDeniedHeaders(data.responseHeaders)
    return data
}

const POSTHOG_PATHS_TO_IGNORE = ['/s/', '/e/', '/i/']
// want to ignore posthog paths when capturing requests, or we can get trapped in a loop
// because calls to PostHog would be reported using a call to PostHog which would be reported....
const ignorePostHogPaths = (
    data: CapturedNetworkRequest,
    apiHostConfig: PostHogConfig['api_host'],
    isIngestionEndpoint?: (url: string) => boolean
): CapturedNetworkRequest | undefined => {
    if (isIngestionEndpoint?.(data.name)) {
        return undefined
    }

    const url = convertToURL(data.name)

    // we need to account for api host config as e.g. pathname could be /ingest/s/ and we want to ignore that
    let replaceValue = apiHostConfig.indexOf('http') === 0 ? convertToURL(apiHostConfig)?.pathname : apiHostConfig
    if (replaceValue === '/') {
        replaceValue = ''
    }
    const pathname = url?.pathname.replace(replaceValue || '', '')

    if (url && pathname && POSTHOG_PATHS_TO_IGNORE.some((path) => pathname.indexOf(path) === 0)) {
        return undefined
    }
    return data
}

function estimateBytes(payload: string): number {
    return new Blob([payload]).size
}

function enforcePayloadSizeLimit(
    payload: string | null | undefined,
    headers: Record<string, any> | undefined,
    limit: number,
    description: string
): string | null | undefined {
    if (isNullish(payload)) {
        return payload
    }

    let requestContentLength: string | number = headers?.['content-length'] || estimateBytes(payload)
    if (isString(requestContentLength)) {
        requestContentLength = parseInt(requestContentLength)
    }

    if (requestContentLength > limit) {
        return LOGGER_PREFIX + ` ${description} body too large to record (${requestContentLength} bytes)`
    }

    return payload
}

// people can have arbitrarily large payloads on their site, but we don't want to ingest them
const limitPayloadSize = (
    options: NetworkRecordOptions
): ((data: CapturedNetworkRequest | undefined) => CapturedNetworkRequest | undefined) => {
    const limit = effectivePayloadLimitBytes(options)

    return (data) => {
        if (data?.requestBody) {
            data.requestBody = enforcePayloadSizeLimit(data.requestBody, data.requestHeaders, limit, 'Request')
        }

        if (data?.responseBody) {
            data.responseBody = enforcePayloadSizeLimit(data.responseBody, data.responseHeaders, limit, 'Response')
        }

        return data
    }
}

function scrubPayload(payload: string | null | undefined, label: 'Request' | 'Response'): string | null | undefined {
    if (isNullish(payload)) {
        return payload
    }
    let scrubbed = payload

    if (!shouldCaptureValue(scrubbed, false)) {
        scrubbed = LOGGER_PREFIX + ' ' + label + ' body ' + REDACTED
    }
    each(PAYLOAD_CONTENT_DENY_LIST, (text) => {
        if (scrubbed?.length && scrubbed?.indexOf(text) !== -1) {
            scrubbed = LOGGER_PREFIX + ' ' + label + ' body ' + REDACTED + ' as might contain: ' + text
        }
    })

    return scrubbed
}

function scrubPayloads(capturedRequest: CapturedNetworkRequest | undefined): CapturedNetworkRequest | undefined {
    if (isUndefined(capturedRequest)) {
        return undefined
    }

    capturedRequest.requestBody = scrubPayload(capturedRequest.requestBody, 'Request')
    capturedRequest.responseBody = scrubPayload(capturedRequest.responseBody, 'Response')

    return capturedRequest
}

const initialMaskFallbackRequests = new WeakSet<CapturedNetworkRequest>()

export const isInitialMaskFallback = (request: CapturedNetworkRequest | undefined): boolean =>
    !!request && initialMaskFallbackRequests.has(request)

/**
 *  whether a maskRequestFn is provided or not,
 *  we ensure that we remove the denied header from requests
 *  we _never_ want to record that header by accident
 *  if someone complains then we'll add an opt-in to let them override it
 */
export const buildNetworkRequestOptions = (
    instanceConfig: PostHogConfig,
    remoteNetworkOptions: Pick<
        NetworkRecordOptions,
        'recordHeaders' | 'recordBody' | 'recordPerformance' | 'payloadHostDenyList'
    >,
    isIngestionEndpoint?: (url: string) => boolean
): NetworkRecordOptions => {
    const config: NetworkRecordOptions = {
        payloadSizeLimitBytes: defaultNetworkOptions.payloadSizeLimitBytes,
        performanceEntryTypeToObserve: [...defaultNetworkOptions.performanceEntryTypeToObserve],
        payloadHostDenyList: [
            ...(remoteNetworkOptions.payloadHostDenyList || []),
            ...defaultNetworkOptions.payloadHostDenyList,
        ],
    }
    // client can always disable despite remote options
    const canRecordHeaders =
        instanceConfig.session_recording.recordHeaders === false ? false : remoteNetworkOptions.recordHeaders
    const canRecordBody =
        instanceConfig.session_recording.recordBody === false ? false : remoteNetworkOptions.recordBody
    const canRecordPerformance =
        instanceConfig.capture_performance === false ? false : remoteNetworkOptions.recordPerformance

    const payloadLimiter = limitPayloadSize(config)

    const enforcedCleaningFn: NetworkRecordOptions['maskRequestFn'] = (d: CapturedNetworkRequest) =>
        payloadLimiter(ignorePostHogPaths(removeAuthorizationHeader(d), instanceConfig.api_host, isIngestionEndpoint))

    const hasDeprecatedMaskFunction = isFunction(instanceConfig.session_recording.maskNetworkRequestFn)

    if (hasDeprecatedMaskFunction && isFunction(instanceConfig.session_recording.maskCapturedNetworkRequestFn)) {
        logger.warn(
            'Both `maskNetworkRequestFn` and `maskCapturedNetworkRequestFn` are defined. `maskNetworkRequestFn` will be ignored.'
        )
    }

    if (hasDeprecatedMaskFunction) {
        instanceConfig.session_recording.maskCapturedNetworkRequestFn = (data: CapturedNetworkRequest) => {
            const cleanedURL = instanceConfig.session_recording.maskNetworkRequestFn!({ url: data.name })
            // Preserve the nullish signal for initial entries so the required-metadata fallback below can
            // remove all customer-controlled content. Keep the deprecated URL-only behavior otherwise.
            if (!cleanedURL && data.isInitial) {
                return cleanedURL
            }
            // the deprecated mask fn can suppress the URL, leaving `name` undefined on purpose
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
            return {
                ...data,
                name: cleanedURL?.url,
            } as CapturedNetworkRequest
        }
    }

    config.maskRequestFn = isFunction(instanceConfig.session_recording.maskCapturedNetworkRequestFn)
        ? (data) => {
              const cleanedRequest = enforcedCleaningFn(data)
              if (!cleanedRequest) {
                  return undefined
              }

              // Initial entries are required performance metadata, but their URL is still customer data.
              // Keep only required, non-content fields before invoking the callback because callbacks may
              // mutate their argument. In particular, do not copy customer-controlled server timing data.
              const requiredInitialMetadata: CapturedNetworkRequest | undefined = cleanedRequest.isInitial
                  ? {
                        name: '',
                        entryType: cleanedRequest.entryType,
                        startTime: cleanedRequest.startTime,
                        duration: cleanedRequest.duration,
                        endTime: cleanedRequest.endTime,
                        timeOrigin: cleanedRequest.timeOrigin,
                        timestamp: cleanedRequest.timestamp,
                        isInitial: true,
                    }
                  : undefined
              const maskedRequest = instanceConfig.session_recording.maskCapturedNetworkRequestFn?.(cleanedRequest)

              // A nullish result normally drops the request. Initial timing metadata must remain for replay,
              // so retain it without the URL or any network content rather than exposing deliberately filtered data.
              if (isNullish(maskedRequest) && requiredInitialMetadata) {
                  initialMaskFallbackRequests.add(requiredInitialMetadata)
                  return requiredInitialMetadata
              }
              return maskedRequest ?? undefined
          }
        : (data) => scrubPayloads(enforcedCleaningFn(data))

    return {
        ...defaultNetworkOptions,
        ...config,
        recordHeaders: canRecordHeaders,
        recordBody: canRecordBody,
        recordPerformance: canRecordPerformance,
        recordInitialRequests: canRecordPerformance,
        streamNetworkBody: instanceConfig.session_recording.streamNetworkBody === true,
    }
}
